// Client engagement timeline — chronological history of one client's portal / document / E-Sign / ID actions.
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { FileUp, PenLine, UserRound, CheckCircle2, MonitorSmartphone, Loader2 } from 'lucide-react';
import { ActiveNow, timeAgo } from './LiveClientActivity';

const API = process.env.REACT_APP_BACKEND_URL;
const ICON = { portal: MonitorSmartphone, documents: FileUp, esign: PenLine, idv: UserRound, completed: CheckCircle2 };
const TONE = { portal: 'text-indigo-600 bg-indigo-50', documents: 'text-sky-600 bg-sky-50', esign: 'text-violet-600 bg-violet-50', idv: 'text-amber-600 bg-amber-50', completed: 'text-emerald-600 bg-emerald-50' };

function dayLabel(iso) {
  const d = new Date(iso); const t = new Date(); const y = new Date(); y.setDate(t.getDate() - 1);
  const same = (a, b) => a.toDateString() === b.toDateString();
  if (same(d, t)) return 'Today';
  if (same(d, y)) return 'Yesterday';
  return d.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short', year: d.getFullYear() !== t.getFullYear() ? 'numeric' : undefined });
}
const fmtTime = (iso) => new Date(iso).toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit' }).toUpperCase();

export default function ClientActivityTimeline({ appId, headers }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    let alive = true;
    const load = () => axios.get(`${API}/api/client-activity/${appId}`, { headers }).then(({ data: d }) => alive && setData(d)).catch(() => alive && setData({ items: [], error: true }));
    load(); const t = setInterval(load, 15000);
    return () => { alive = false; clearInterval(t); };
  }, [appId, headers]);
  if (!data) return <div className="flex items-center gap-2 text-xs text-slate-400 py-4"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading engagement history…</div>;
  const groups = [];
  const multi = new Set(data.items.map((i) => i.client)).size > 1;
  data.items.forEach((it) => { const l = dayLabel(it.at); const g = groups[groups.length - 1]; if (g && g.label === l) g.items.push(it); else groups.push({ label: l, items: [it] }); });
  return (
    <div data-testid="cat-timeline">
      <div className="flex flex-wrap items-center gap-2 mb-3 text-xs text-slate-500">
        {data.active_now ? <ActiveNow /> : data.last_client_activity_at ? <span data-testid="cat-last">Last active {timeAgo(data.last_client_activity_at)}</span> : <span>No client activity yet</span>}
        <span className="text-slate-300">·</span><span>{data.total} action{data.total === 1 ? '' : 's'} recorded</span>
      </div>
      {groups.length === 0 && <p className="text-sm text-slate-500 py-4" data-testid="cat-empty">The client hasn&apos;t opened their portal yet — engagement will appear here the moment they do.</p>}
      <div className="space-y-5">
        {groups.map((g) => (
          <section key={g.label} data-testid={`cat-day-${g.label.replace(/\W+/g, '-')}`}>
            <p className="text-[10px] font-black uppercase tracking-[0.16em] text-slate-400 mb-2">{g.label}</p>
            <ul className="relative pl-5 before:absolute before:left-[7px] before:top-2 before:bottom-2 before:w-px before:bg-slate-200 space-y-2">
              {g.items.map((it) => { const Icon = ICON[it.kind] || MonitorSmartphone; return (
                <li key={it.id} className="relative flex items-start gap-3" data-testid={`cat-item-${it.id}`}>
                  <span className={`absolute -left-5 top-0.5 grid h-4 w-4 place-items-center rounded-full ${TONE[it.kind] || TONE.portal}`}><Icon className="w-2.5 h-2.5" /></span>
                  <span className="w-[74px] shrink-0 text-[11px] font-bold tabular-nums text-slate-500 pt-px">{fmtTime(it.at)}</span>
                  <span className="text-sm text-slate-800 leading-snug"><span className={it.major ? 'font-bold' : ''}>{it.label}</span>{multi && it.client && <span className="text-slate-400 text-xs"> · {it.client}</span>}</span>
                </li>
              ); })}
            </ul>
          </section>
        ))}
      </div>
    </div>
  );
}
